import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip as ChartTooltipPlugin, Filler } from "chart.js";
import { Line } from "react-chartjs-2";
import { lightThemeColors } from "../../assets/styles/colors";
import PageHeader from "../../Components/Layout/PageHeader";
import { useLicensingContext } from "../../context/useLicensingContext";
import { useProjectContext } from "../../context/useProjectContext";
import { useOutletContext } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import PlansSummaryCard from "./PlansSummaryCard";
import ProductDropdown from "./ProductDropdown";
import NoSubscriptionPage from "./NoSubscription";
import AppDateRangePicker from "../../Components/Generic/AppDateRangePicker";
import { Skeleton } from "../../Components/Generic/Skeleton";
import { PRODUCTS, ROUTE_MAP, CARD_COLOR, renderProductIcon } from "../../constants/productConstants";
import { Info, ArrowDown, ArrowLeft, BarChart3, ChartLine, Check, Activity, MoveUp, MoveDown } from "lucide-react";
import { useUserContext } from "../../context/useUserContext";
import { getFirstName, getTimeGreeting } from "../../helper";
import { formatNumber, formatCompactNumber } from "../../helper/numberFormat";
import Tooltip from "../../Components/Generic/Tooltip";
import { PRODUCT_METRICS } from "../../constants/OptionsConstants";
import {
  getDefaultMonthToDateRange,
  formatDateRangeForAPI,
  formatBucketLabel,
  periodLabelFor,
  timeAgo,
} from "../../helper/dateFormat";
import { rangeFitsInterval, intervalLabel } from "../../helper/subscriptionHelper";
import { hexToRgba } from "../../helper/colorHelper";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltipPlugin, Filler);

const deriveProductLimits = (items = []) => {
  const map = {};
  items.forEach((item) => {
    map[item.product_slug] = item;
  });
  return map;
};

const limitFor = (productLimits, slug, limitKey) => {
  const raw = productLimits?.[slug]?.[limitKey];
  if (raw === undefined) return undefined;
  if (raw === null) return null;
  return limitKey === "tokens" ? Math.round(raw * 0.75) : raw;
};

const limitTextFor = (limit) => {
  if (limit === null) return "Unlimited";
  if (limit === undefined) return "";
  return "of " + formatNumber(limit);
};

const percentFor = (value, limit) => {
  if (limit === null || limit === undefined) return null;
  if (!limit) return 0;
  return Math.min((Number(value || 0) / limit) * 100, 100);
};

const TrendValue = ({ pct }) => {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return "—";
  const rounded = Math.round(pct);
  const Arrow = rounded >= 0 ? MoveUp : MoveDown;
  return (
    <>
      <Arrow size={10} strokeWidth={2} aria-hidden="true" />
      {Math.abs(rounded)}%
    </>
  );
};

const trendColor = (pct) => {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return "#98A2B3";
  return pct >= 0 ? "#067647" : "#B42318";
};

const ACTIVITY_DOT_COLORS = {
  api_key_created: "#12B76A",
  api_key_deleted: "#98A2B3",
  member_joined: "#7F56D9",
  conference_created: "#2E90FA",
  meeting_hosted: "#06AED4",
  nearing_limit: "#F97316",
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject, getRecentActivity } = useProjectContext();
  const { user } = useUserContext();
  const firstName = getFirstName(user);
  const greeting = getTimeGreeting();
  const { setLoading } = useOutletContext();
  const {
    subscription,
    setSubscription,
    getPlatformSubscription,
    getPlatformProducts,
    getProductUsageCards,
    getProductUsageTrend,
  } = useLicensingContext();

  const tableRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState(getDefaultMonthToDateRange);
  const [usageCards, setUsageCards] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [activeOnly, setActiveOnly] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState("all");
  const [trendPoints, setTrendPoints] = useState([]);
  const [trendGranularity, setTrendGranularity] = useState("day");
  const [isTrendLoading, setIsTrendLoading] = useState(true);
  const [activityEvents, setActivityEvents] = useState([]);
  const [isActivityLoading, setIsActivityLoading] = useState(true);
  const [hiddenSeries, setHiddenSeries] = useState({});
  const [availableProducts, setAvailableProducts] = useState([]);

  const items = subscription?.items || [];
  const products = items.map((item) => ({ product: item.product_slug, label: item.product_name }));
  const productLimits = deriveProductLimits(items);
  const subscribedSlugs = new Set(items.map((i) => i.product_slug));
  const unsubscribedProducts = availableProducts.filter(({ slug }) => !subscribedSlugs.has(slug));

  const trackedProducts = products.filter(({ product }) => (PRODUCT_METRICS[product] || []).length > 0);
  const cardsLoading = usageCards === null;
  const intervalFor = (slug) => {
    const item = items.find((i) => i.product_slug === slug);
    if (item?.manual_payment) return subscription?.manual_subscription?.interval || subscription?.interval;
    return subscription?.interval;
  };
  const showLimitsFor = (slug) => rangeFitsInterval(dateRange, intervalFor(slug));
  const hiddenLimitIntervals = [
    ...new Set(trackedProducts.filter(({ product }) => !showLimitsFor(product)).map(({ product }) => intervalFor(product))),
  ];
  const someLimitsShown = trackedProducts.some(({ product }) => showLimitsFor(product));

  const trackedProductKey = trackedProducts.map(({ product }) => product).join(",");
  const isSelectedProductTracked = trackedProducts.some(({ product }) => product === selectedProduct);

  useEffect(() => {
    if (!isSelectedProductTracked && trackedProducts.length > 0) {
      setSelectedProduct(trackedProducts[0].product);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackedProductKey, isSelectedProductTracked]);

  useEffect(() => {
    setHiddenSeries({});
  }, [selectedProduct]);

  const handleGetUsage = () => {
    if (!selectedProject || !dateRange || trackedProducts.length === 0) return;
    const { start_date, end_date } = formatDateRangeForAPI(dateRange[0], dateRange[1]);
    const params = { start_date, end_date };

    const results = {};
    let remaining = trackedProducts.length;
    trackedProducts.forEach(({ product }) => {
      getProductUsageCards(
        selectedProject.id,
        product,
        params,
        (response) => {
          results[product] = response.data || {};
          remaining -= 1;
          if (remaining === 0) setUsageCards({ ...results });
        },
        () => {
          remaining -= 1;
          if (remaining === 0) setUsageCards({ ...results });
        }
      );
    });
  };

  useEffect(() => {
    handleGetUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, dateRange, trackedProducts.length]);

  useEffect(() => {
    if (!selectedProject || !dateRange) return;

    if (!isSelectedProductTracked) {
      if (trackedProducts.length === 0) {
        setTrendPoints([]);
        setIsTrendLoading(false);
      }
      return;
    }

    setIsTrendLoading(true);
    const { start_date, end_date } = formatDateRangeForAPI(dateRange[0], dateRange[1]);
    const params = { start_date, end_date, tz: Intl.DateTimeFormat().resolvedOptions().timeZone };

    getProductUsageTrend(
      selectedProject.id,
      selectedProduct,
      params,
      (response) => {
        setTrendPoints(response.data?.points || []);
        setTrendGranularity(response.data?.granularity || "day");
        setIsTrendLoading(false);
      },
      () => {
        setTrendPoints([]);
        setIsTrendLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject, dateRange, selectedProduct, trackedProductKey]);

  useEffect(() => {
    if (!selectedProject) return;
    setIsActivityLoading(true);
    getRecentActivity(
      selectedProject.id,
      (response) => {
        setActivityEvents(response.data?.events || []);
        setIsActivityLoading(false);
      },
      () => {
        setActivityEvents([]);
        setIsActivityLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) return;
    setLoading(true);
    setIsLoading(true);
    getPlatformSubscription(
      selectedProject.id,
      () => {
        setLoading(false);
        setIsLoading(false);
        getPlatformProducts(
          selectedProject.id,
          (response) => {
            const apiProducts = response?.data?.products || [];
            setAvailableProducts(apiProducts.filter((p) => p.active).map((p) => ({ slug: p.slug, name: p.name })));
          },
          () => {}
        );
      },
      () => {
        setSubscription(null);
        setLoading(false);
        setIsLoading(false);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const rowFor = ({ product, label }) => {
    const metrics = PRODUCT_METRICS[product] || [];
    const card = usageCards?.[product];
    const byKey = {};
    metrics.forEach((m) => {
      const value = card?.current?.[m.key] ?? 0;
      const limit = limitFor(productLimits, product, m.limitKey);
      byKey[m.key] = {
        text: formatNumber(value),
        percent: percentFor(value, limit) ?? 0,
        limitText: limitTextFor(limit),
        hasData: true,
      };
    });

    const maxPercent = Math.max(0, ...Object.values(byKey).map((c) => c.percent || 0));

    const topMetric = metrics.reduce((best, m) => {
      const pct = byKey[m.key]?.percent || 0;
      return !best || pct > (byKey[best.key]?.percent || 0) ? m : best;
    }, null);
    const topPct = topMetric ? card?.percent_change?.[topMetric.key] : null;
    const topValue = topMetric ? Number(card?.current?.[topMetric.key] ?? 0) : 0;

    return {
      slug: product,
      name: label || PRODUCTS[product]?.title || product,
      words: byKey.words || null,
      audio: byKey.audio || null,
      video: byKey.video || null,
      maxPercent,
      warning: maxPercent >= 85,
      trendPct: topPct,
      topMetricLabel: topMetric?.label,
      topMetricValue: topValue,
      route: ROUTE_MAP[product],
    };
  };

  const allRows = trackedProducts.map(rowFor);
  const filteredRows = activeOnly ? allRows.filter((r) => r.maxPercent > 0) : allRows;
  const sortedRows = filteredRows.slice().sort((a, b) => {
    if (sortBy === "usage") return b.maxPercent - a.maxPercent;
    return a.name.localeCompare(b.name);
  });

  const topProductsPeriodLabel = periodLabelFor(dateRange);

  const realAttentionRows = allRows
    .filter((r) => r.warning)
    .slice()
    .sort((a, b) => b.maxPercent - a.maxPercent);

  const attentionRows = realAttentionRows;
  const topAttentionRow = attentionRows[0];

  const nearingLimitEvents = realAttentionRows.map((r) => ({
    type: "nearing_limit",
    text: `${r.name} is nearing its ${r.topMetricLabel.toLowerCase()} limit`,
    created_at: new Date().toISOString(),
  }));
  const feedEvents = [...activityEvents, ...nearingLimitEvents]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8);

  const topProducts = allRows
    .filter((r) => r.maxPercent > 0)
    .slice()
    .sort((a, b) => b.maxPercent - a.maxPercent)
    .slice(0, 3);

  const heroIsAll = selectedProduct === "all";
  const wordsProducts = trackedProducts.filter(({ product }) => (PRODUCT_METRICS[product] || []).some((m) => m.key === "words"));
  const totalWords = wordsProducts.reduce((sum, { product }) => sum + Number(usageCards?.[product]?.current?.words ?? 0), 0);
  const totalPrevWords = wordsProducts.reduce((sum, { product }) => sum + Number(usageCards?.[product]?.previous?.words ?? 0), 0);
  const totalDeltaPct = totalPrevWords > 0 ? ((totalWords - totalPrevWords) / totalPrevWords) * 100 : (totalWords > 0 ? 100 : null);

  const heroProduct = trackedProducts.find(({ product }) => product === selectedProduct);
  const heroMetrics = heroProduct
    ? (PRODUCT_METRICS[heroProduct.product] || []).map((m) => {
        const value = usageCards?.[heroProduct.product]?.current?.[m.key] ?? 0;
        const limit = limitFor(productLimits, heroProduct.product, m.limitKey);
        return {
          key: m.key,
          label: m.label,
          value: formatNumber(value),
          delta: usageCards?.[heroProduct.product]?.percent_change?.[m.key],
          percent: percentFor(value, limit) ?? 0,
          limitText: limitTextFor(limit),
        };
      })
    : [];

  const CHART_COLORS = { words: "#7F56D9", audio: "#F97316", video: "#12B76A" };

  const chartSeries = heroIsAll
    ? [{ key: "words", label: "Words", color: CHART_COLORS.words }]
    : (PRODUCT_METRICS[heroProduct?.product] || []).map((m) => {
        const metricInfo = heroMetrics.find((hm) => hm.key === m.key);
        return {
          key: m.key,
          label: m.label,
          color: CHART_COLORS[m.key] || CHART_COLORS.words,
          value: metricInfo?.value,
          delta: metricInfo?.delta,
          limitText: metricInfo?.limitText,
        };
      });

  const hasChartData = trendPoints.length > 1 && chartSeries.length > 0;

  const axisIdFor = (key) => (key === "words" ? "words" : "minutes");

  const primarySeriesKey = chartSeries[0]?.key;
  const peakIndex = hasChartData && primarySeriesKey
    ? trendPoints.reduce((best, p, i) => (Number(p[primarySeriesKey] || 0) > Number(trendPoints[best]?.[primarySeriesKey] || 0) ? i : best), 0)
    : -1;
  const peakInfo = peakIndex >= 0 && Number(trendPoints[peakIndex]?.[primarySeriesKey] || 0) > 0
    ? {
        label: chartSeries[0].label,
        value: formatNumber(trendPoints[peakIndex][primarySeriesKey]),
        date: formatBucketLabel(trendPoints[peakIndex].date, trendGranularity, true),
      }
    : null;

  const chartData = hasChartData ? {
    labels: trendPoints.map((p) => formatBucketLabel(p.date, trendGranularity)),
    datasets: chartSeries.map((s) => ({
      label: s.label,
      data: trendPoints.map((p) => Number(p[s.key] || 0)),
      hidden: !!hiddenSeries[s.key],
      yAxisID: axisIdFor(s.key),
      borderColor: s.color,
      backgroundColor: (context) => {
        const { ctx, chartArea } = context.chart;
        if (!chartArea) return hexToRgba(s.color, 0.15);
        const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
        gradient.addColorStop(0, hexToRgba(s.color, 0.28));
        gradient.addColorStop(1, hexToRgba(s.color, 0));
        return gradient;
      },
      fill: heroIsAll,
      tension: 0.4,
      borderWidth: 2.5,
      borderCapStyle: "round",
      borderJoinStyle: "round",
      pointRadius: s.key === primarySeriesKey ? trendPoints.map((_, i) => (i === peakIndex ? 4 : 0)) : 0,
      pointBackgroundColor: s.color,
      pointBorderColor: "#ffffff",
      pointBorderWidth: 2,
      pointHoverRadius: 5,
      pointHoverBackgroundColor: "#ffffff",
      pointHoverBorderColor: s.color,
      pointHoverBorderWidth: 2.5,
      pointHitRadius: 12,
    })),
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#101828",
        titleColor: "#98A2B3",
        titleFont: { size: 11, weight: "600" },
        bodyColor: "#ffffff",
        bodyFont: { size: 12, weight: "700" },
        padding: 10,
        cornerRadius: 8,
        displayColors: true,
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        callbacks: {
          title: (items) => {
            const point = trendPoints[items[0]?.dataIndex];
            return point ? formatBucketLabel(point.date, trendGranularity, true) : "";
          },
          label: (item) => `${item.dataset.label}: ${formatNumber(item.parsed.y)}`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: "#F9FAFB" },
        ticks: { color: "#98A2B3", font: { size: 11 }, maxRotation: 0, autoSkip: true, maxTicksLimit: 7 },
      },
      ...(chartSeries.some((s) => axisIdFor(s.key) === "words") ? {
        words: {
          position: "left",
          grid: { color: "#F2F4F7", drawTicks: true, tickLength: 4, tickColor: "#E4E7EC" },
          border: { display: true, color: "#E4E7EC" },
          ticks: { color: "#98A2B3", font: { size: 11 }, maxTicksLimit: 5, callback: (value) => formatCompactNumber(value) },
        },
      } : {}),
      ...(chartSeries.some((s) => axisIdFor(s.key) === "minutes") ? {
        minutes: {
          position: "right",
          grid: { display: true, drawOnChartArea: false, drawTicks: true, tickLength: 4, tickColor: "#E4E7EC" },
          border: { display: true, color: "#E4E7EC" },
          ticks: { color: "#98A2B3", font: { size: 11 }, maxTicksLimit: 5, callback: (value) => formatCompactNumber(value) },
        },
      } : {}),
    },
  };

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <ClipLoader size={50} color={lightThemeColors.primaryColor} />
        </LoadingContainer>
      </Container>
    );
  }

  if (!subscription) {
    return (
      <Container>
        <PageContent>
          <NoSubscriptionPage />
        </PageContent>
      </Container>
    );
  }

  return (
    <Container>
      <PageContent>
        <PageTop>
          <PageHeader
            title={
              <>
                {greeting}
                {firstName && <>, <span data-emotii-exclude>{firstName}</span></>}
              </>
            }
            subtitle="Here's what's happening across your account today."
          />
        </PageTop>

        <Section>
          <SectionHeader>
            <SectionTitle>{products.length <= 1 ? "Your Plan" : "Your Plans"}</SectionTitle>
          </SectionHeader>
          <PlansSummaryCard
            items={items}
            subscription={subscription}
            unsubscribedProducts={unsubscribedProducts}
            onViewAll={() => navigate("/subscriptions")}
          />
        </Section>

        <HeroRow>
          <HeroCard>
            <HeroTop>
              <ProductDropdown
                value={selectedProduct}
                onValueChange={setSelectedProduct}
                options={trackedProducts.map(({ product, label }) => ({
                  value: product,
                  label: label || PRODUCTS[product]?.title || product,
                }))}
              />
              <AppDateRangePicker value={dateRange} onChange={setDateRange} />
            </HeroTop>

            {heroIsAll && (
              <HeroValueRow>
                <HeroValue>{formatNumber(totalWords)}</HeroValue>
                <HeroLabel>words across all products</HeroLabel>
                <TrendPill $color={trendColor(totalDeltaPct)}><TrendValue pct={totalDeltaPct} /></TrendPill>
              </HeroValueRow>
            )}

            {!heroIsAll && isTrendLoading && <ChartSkeleton />}

            {!heroIsAll && !isTrendLoading && !hasChartData && (
              <EmptyStateWrap>
                <EmptyStateIcon>
                  <ChartLine size={20} color="#98A2B3" />
                </EmptyStateIcon>
                <EmptyStateTitle>No usage yet {topProductsPeriodLabel}</EmptyStateTitle>
                <EmptyStateSubtext>Usage for this product will show up here once it's used</EmptyStateSubtext>
              </EmptyStateWrap>
            )}

            {!isTrendLoading && hasChartData && (
              <ChartBlock>
                <ChartCanvasWrapper>
                  {chartSeries.some((s) => axisIdFor(s.key) === "words") && (
                    <AxisTitleBadge $side="left">Words</AxisTitleBadge>
                  )}
                  {chartSeries.some((s) => axisIdFor(s.key) === "minutes") && (
                    <AxisTitleBadge $side="right">Minutes</AxisTitleBadge>
                  )}
                  {peakInfo && (
                    <PeakBadge>Peak: {peakInfo.value} {peakInfo.label.toLowerCase()} on {peakInfo.date}</PeakBadge>
                  )}
                  <Line data={chartData} options={chartOptions} />
                </ChartCanvasWrapper>
                {!heroIsAll && (
                  <ChartLegendRow>
                    {chartSeries.map((s) => {
                      const showAxisHint = new Set(chartSeries.map((cs) => axisIdFor(cs.key))).size > 1;
                      const isHidden = !!hiddenSeries[s.key];
                      return (
                        <ChartLegendItem
                          key={s.key}
                          type="button"
                          $dim={isHidden}
                          onClick={() => setHiddenSeries((prev) => ({ ...prev, [s.key]: !prev[s.key] }))}
                        >
                          <ChartLegendDot style={{ background: s.color }} />
                          <ChartLegendTextCol>
                            <ChartLegendLabel>
                              {s.label}{showAxisHint ? ` (${axisIdFor(s.key) === "words" ? "left" : "right"} axis)` : ""}
                            </ChartLegendLabel>
                            <ChartLegendValueRow>
                              <ChartLegendValue>{s.value}</ChartLegendValue>
                              <TrendPill $compact $color={trendColor(s.delta)}><TrendValue pct={s.delta} /></TrendPill>
                            </ChartLegendValueRow>
                          </ChartLegendTextCol>
                        </ChartLegendItem>
                      );
                    })}
                  </ChartLegendRow>
                )}
              </ChartBlock>
            )}
          </HeroCard>

          <SidebarCard as={TopProductsCard}>
            <SidebarTitleRow>
              <TitleWithInfo>
                <SidebarTitle>Top products {topProductsPeriodLabel}</SidebarTitle>
                <Tooltip
                  side="bottom"
                  content={
                    <TooltipText>
                      This ranks products by how close they are to running out, not by how much
                      they've actually been used. A product with a small limit can rank higher than
                      one that's been used a lot more, if it has less room left.
                    </TooltipText>
                  }
                >
                  <InfoIconButton type="button" aria-label="How Top products is calculated">
                    <Info size={14} />
                  </InfoIconButton>
                </Tooltip>
              </TitleWithInfo>
            </SidebarTitleRow>
            {cardsLoading ? (
              <TopProductsSkeleton />
            ) : topProducts.length === 0 ? (
              <EmptyStateWrap>
                <EmptyStateIcon>
                  <BarChart3 size={20} color="#98A2B3" />
                </EmptyStateIcon>
                <EmptyStateTitle>No usage yet {topProductsPeriodLabel}</EmptyStateTitle>
                <EmptyStateSubtext>Rankings will show up here once a product is used</EmptyStateSubtext>
              </EmptyStateWrap>
            ) : (
              <TopProductsList>
                {topProducts.map((p, i) => (
                  <div key={p.slug}>
                    <LeaderRow>
                      <RankNumber>{String(i + 1).padStart(2, "0")}</RankNumber>
                      <PinnedBadge style={{ background: "#F4EBFF", color: CARD_COLOR }}>{renderProductIcon(p.slug, CARD_COLOR)}</PinnedBadge>
                      <LeaderName>
                        {p.name}
                        <LeaderMetricLabel> · {p.topMetricLabel}</LeaderMetricLabel>
                      </LeaderName>
                      <LeaderValue>{formatNumber(p.topMetricValue)}</LeaderValue>
                    </LeaderRow>
                    <LeaderBarTrack>
                      <LeaderBarFill style={{ width: `${p.maxPercent}%` }} />
                    </LeaderBarTrack>
                  </div>
                ))}
              </TopProductsList>
            )}
            {topProducts.length > 0 && (
              <ViewBreakdownLink
                type="button"
                onClick={() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                View full breakdown
                <ArrowDown size={14} />
              </ViewBreakdownLink>
            )}
          </SidebarCard>
        </HeroRow>

        <MainBody>
          <TableCard ref={tableRef}>
            <TableHeaderRow>
              <TableTitle>Usage by product</TableTitle>
              <TableControls>
                <ControlLabel>Sort:</ControlLabel>
                <SortButton $active={sortBy === "name"} onClick={() => setSortBy("name")}>Name</SortButton>
                <SortButton $active={sortBy === "usage"} onClick={() => setSortBy("usage")}>Most active</SortButton>
                <Divider />
                <ToggleButton onClick={() => setActiveOnly((v) => !v)}>
                  <ToggleTrack $on={activeOnly}>
                    <ToggleKnob $on={activeOnly} />
                  </ToggleTrack>
                  Hide zero usage
                </ToggleButton>
              </TableControls>
            </TableHeaderRow>

            {hiddenLimitIntervals.length > 0 && (
              <LimitsHiddenNote>
                <Info size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                <span>
                  {someLimitsShown ? (
                    <>This range is longer than the <b>{hiddenLimitIntervals.map(intervalLabel).join(" / ")} billing cycle</b> of some products, so their plan limits are hidden. </>
                  ) : (
                    <>This range is longer than your <b>{hiddenLimitIntervals.map(intervalLabel).join(" / ")} billing cycle</b>, so plan limits are hidden. </>
                  )}
                  Limits apply to one cycle at a time. Pick a shorter range to see usage against your limits.
                </span>
              </LimitsHiddenNote>
            )}

            <TableColumnHeaders>
              <span>Product</span>
              <span>Words</span>
              <span>Audio mins</span>
              <span>Video mins</span>
              <span style={{ textAlign: "right" }}>Trend</span>
            </TableColumnHeaders>

            {cardsLoading && <TableSkeletonRows />}

            {!cardsLoading && sortedRows.length === 0 && (
              <EmptyTableNote>No usage yet for the selected period.</EmptyTableNote>
            )}

            {!cardsLoading && sortedRows.map((row) => (
              <TableRow
                key={row.slug}
                $warning={showLimitsFor(row.slug) && row.warning}
              >
                <NameCell>
                  <NameIconBadge>{renderProductIcon(row.slug, CARD_COLOR)}</NameIconBadge>
                  {row.route ? (
                    <ProductLink to={{ pathname: row.route, search: location.search }}>{row.name}</ProductLink>
                  ) : (
                    <span>{row.name}</span>
                  )}
                  {showLimitsFor(row.slug) && row.warning && <WarningBadge>NEAR LIMIT</WarningBadge>}
                </NameCell>
                <MetricCell metric={row.words} showLimit={showLimitsFor(row.slug)} />
                <MetricCell metric={row.audio} showLimit={showLimitsFor(row.slug)} />
                <MetricCell metric={row.video} showLimit={showLimitsFor(row.slug)} />
                <TrendCell style={{ color: trendColor(row.trendPct) }}><TrendValue pct={row.trendPct} /></TrendCell>
              </TableRow>
            ))}
          </TableCard>

          <Sidebar>
            <SidebarCard>
              <SidebarTitleRow>
                <SidebarTitle>Recent activity</SidebarTitle>
              </SidebarTitleRow>
              {(isActivityLoading || cardsLoading) && <FeedSkeleton />}
              {!isActivityLoading && !cardsLoading && feedEvents.length === 0 && (
                <EmptyStateWrap>
                  <EmptyStateIcon>
                    <Activity size={20} color="#98A2B3" />
                  </EmptyStateIcon>
                  <EmptyStateTitle>No activity yet this week</EmptyStateTitle>
                  <EmptyStateSubtext>New keys, members, and alerts will show up here</EmptyStateSubtext>
                </EmptyStateWrap>
              )}
              {!isActivityLoading && !cardsLoading && feedEvents.map((a, i) => (
                <FeedRow key={i}>
                  <FeedDot style={{ background: ACTIVITY_DOT_COLORS[a.type] || "#98A2B3" }} />
                  <div>
                    <FeedText>{a.text}</FeedText>
                    <FeedTime>{a.count > 1 ? "Most recent: " : ""}{timeAgo(a.created_at)}</FeedTime>
                  </div>
                </FeedRow>
              ))}
            </SidebarCard>

            <SidebarCard>
              <SidebarTitleRow>
                <SidebarTitle>Needs attention</SidebarTitle>
                {attentionRows.length > 0 && (
                  <WarningBadge>{attentionRows.length} product{attentionRows.length === 1 ? "" : "s"}</WarningBadge>
                )}
              </SidebarTitleRow>

              {cardsLoading ? (
                <AttentionSkeleton />
              ) : topAttentionRow ? (
                <>
                  <AttentionRow>
                    <NameIconBadge style={{ background: "#FFF1E6" }}>
                      {renderProductIcon(topAttentionRow.slug, "#F97316")}
                    </NameIconBadge>
                    <AttentionTextCol>
                      <AttentionName>{topAttentionRow.name}</AttentionName>
                      <AttentionSub>{topAttentionRow.topMetricLabel} · {formatNumber(topAttentionRow.topMetricValue)}</AttentionSub>
                    </AttentionTextCol>
                    <AttentionPercent>{Math.round(topAttentionRow.maxPercent)}%</AttentionPercent>
                  </AttentionRow>
                  <MetricCellBarTrack>
                    <MetricCellBarFill style={{ width: `${topAttentionRow.maxPercent}%`, background: "#F97316" }} />
                  </MetricCellBarTrack>
                  <ViewBreakdownLink
                    type="button"
                    onClick={() => tableRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  >
                    View usage
                    <ArrowLeft size={14} />
                  </ViewBreakdownLink>
                </>
              ) : (
                <EmptyStateWrap>
                  <EmptyStateIcon style={{ background: "#ECFDF3" }}>
                    <Check size={20} color="#12B76A" />
                  </EmptyStateIcon>
                  <EmptyStateTitle>All products are within limits</EmptyStateTitle>
                  <EmptyStateSubtext>Nothing needs attention right now</EmptyStateSubtext>
                </EmptyStateWrap>
              )}
            </SidebarCard>
          </Sidebar>
        </MainBody>
      </PageContent>
    </Container>
  );
};

export default DashboardPage;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow: auto;
  padding: 32px 24px;
  position: relative;
  height: 100%;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

const PageTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionTitle = styled.h2`
  color: ${lightThemeColors.darkColor};
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  margin: 0;
`;

const HeroRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: stretch;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const HeroCard = styled.div`
  flex: 2;
  min-width: 0;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.04), 0 4px 12px rgba(10, 13, 18, 0.06);
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TopProductsCard = styled.div`
  flex: 1;
  min-width: 260px;
`;

const HeroTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeroValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`;

const HeroValue = styled.span`
  font-size: ${(p) => p.$size || 32}px;
  font-weight: 800;
  color: #101828;
  letter-spacing: -0.02em;
`;

const HeroLabel = styled.span`
  font-size: 13px;
  color: #667085;
`;

const TrendPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: ${(p) => (p.$compact ? "11px" : "13px")};
  font-weight: 700;
  line-height: 1;
  color: ${(p) => p.$color};
  background: ${(p) => (p.$color === "#B42318" ? "#FEF3F2" : p.$color === "#98A2B3" ? "#F2F4F7" : "#ECFDF3")};
  border-radius: 9999px;
  padding: ${(p) => (p.$compact ? "2px 7px" : "3px 9px")};
`;

const EmptyStateWrap = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding: 24px 16px;
`;

const EmptyStateIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #F2F4F7;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyStateTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #344054;
`;

const EmptyStateSubtext = styled.span`
  font-size: 13px;
  color: #98A2B3;
  max-width: 220px;
`;

const ChartBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const ChartCanvasWrapper = styled.div`
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 140px;
`;

const AxisTitleBadge = styled.span`
  position: absolute;
  top: 0;
  ${(p) => (p.$side === "left" ? "left: 0;" : "right: 0;")}
  z-index: 2;
  border: 1px solid #E4E7EC;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  background: #ffffff;
`;

const ChartSkeleton = () => (
  <ChartBlock>
    <Skeleton $h="140px" $radius="12px" />
    <ChartLegendRow>
      <Skeleton $w="120px" $h="44px" $radius="10px" />
      <Skeleton $w="120px" $h="44px" $radius="10px" />
      <Skeleton $w="120px" $h="44px" $radius="10px" />
    </ChartLegendRow>
  </ChartBlock>
);

const TopProductsSkeleton = () => (
  <TopProductsList>
    {[0, 1, 2].map((i) => (
      <div key={i}>
        <LeaderRow>
          <Skeleton $w="26px" $h="14px" />
          <Skeleton $w="32px" $h="32px" $radius="8px" />
          <Skeleton $w="100%" $h="15px" />
          <Skeleton $w="40px" $h="16px" />
        </LeaderRow>
        <LeaderBarTrack>
          <Skeleton $h="100%" $radius="9999px" />
        </LeaderBarTrack>
      </div>
    ))}
  </TopProductsList>
);

const TableSkeletonRows = () => (
  <>
    {[0, 1, 2, 3].map((i) => (
      <TableRow key={i}>
        <NameCell>
          <Skeleton $w="24px" $h="24px" $radius="6px" />
          <Skeleton $w="90px" $h="13px" />
        </NameCell>
        <Skeleton $h="12px" $w="70%" />
        <Skeleton $h="12px" $w="70%" />
        <Skeleton $h="12px" $w="70%" />
        <Skeleton $h="12px" $w="70%" />
      </TableRow>
    ))}
  </>
);

const FeedSkeleton = () => (
  <>
    {[0, 1, 2, 3].map((i) => (
      <FeedRow key={i}>
        <Skeleton $w="8px" $h="8px" $radius="50%" />
        <div style={{ flex: 1 }}>
          <Skeleton $h="13px" $w="90%" />
          <Skeleton $h="11px" $w="50%" $radius="4px" />
        </div>
      </FeedRow>
    ))}
  </>
);

const AttentionSkeleton = () => (
  <>
    <AttentionRow>
      <Skeleton $w="24px" $h="24px" $radius="6px" />
      <AttentionTextCol>
        <Skeleton $h="14px" $w="70%" />
        <Skeleton $h="12px" $w="50%" />
      </AttentionTextCol>
      <Skeleton $w="36px" $h="16px" />
    </AttentionRow>
    <MetricCellBarTrack>
      <Skeleton $h="100%" $radius="9999px" />
    </MetricCellBarTrack>
  </>
);

const ChartLegendRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const ChartLegendItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #F9FAFB;
  border: none;
  border-radius: 10px;
  padding: 8px 14px;
  font-size: 12px;
  color: #667085;
  cursor: pointer;
  opacity: ${(p) => (p.$dim ? 0.45 : 1)};
  transition: opacity 0.15s;
`;

const PeakBadge = styled.span`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  border: 1px solid #E9D7FE;
  background: #F9F5FF;
  color: #6941C6;
  border-radius: 6px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

const ChartLegendDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ChartLegendTextCol = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ChartLegendLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #98A2B3;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const ChartLegendValueRow = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ChartLegendValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #101828;
`;

const MainBody = styled.div`
  display: flex;
  gap: 20px;
  align-items: flex-start;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const TableCard = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.04), 0 4px 12px rgba(10, 13, 18, 0.06);
  padding: 18px 20px 12px;
`;

const TableHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const LimitsHiddenNote = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  background: #F9F5FF;
  color: #53389E;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;

  b {
    font-weight: 600;
  }
`;

const TableTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #181D27;
`;

const TableControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlLabel = styled.span`
  font-size: 12px;
  color: #98A2B3;
`;

const SortButton = styled.button`
  border: 1px solid ${(p) => (p.$active ? "#E9D7FE" : "#E9EAEB")};
  background: ${(p) => (p.$active ? "#F4EBFF" : "transparent")};
  color: ${(p) => (p.$active ? "#7F56D9" : "#667085")};
  border-radius: 7px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`;

const Divider = styled.span`
  width: 1px;
  height: 16px;
  background: #E9EAEB;
  margin: 0 2px;
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: #667085;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 4px;
  cursor: pointer;
`;

const ToggleTrack = styled.span`
  width: 28px;
  height: 16px;
  border-radius: 9999px;
  background: ${(p) => (p.$on ? "#7F56D9" : "#E4E7EC")};
  position: relative;
  display: inline-block;
  transition: background 0.15s;
`;

const ToggleKnob = styled.span`
  position: absolute;
  top: 2px;
  left: ${(p) => (p.$on ? "14px" : "2px")};
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ffffff;
  transition: left 0.15s;
`;

const TableColumnHeaders = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1.4fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(80px, 0.7fr);
  gap: 24px;
  padding: 9px 10px;
  background: #F9FAFB;
  border-radius: 8px;

  span {
    font-size: 11px;
    font-weight: 600;
    color: #98A2B3;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: minmax(160px, 1.4fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(120px, 1fr) minmax(80px, 0.7fr);
  gap: 24px;
  align-items: center;
  padding: 10px 10px 10px 8px;
  border-left: 3px solid ${(p) => (p.$warning ? "#F97316" : "transparent")};
  background: ${(p) => (p.$warning ? "#FFFBF6" : "transparent")};
  border-bottom: 1px solid #F2F4F7;
  transition: background 0.12s;

  &:hover {
    background: ${(p) => (p.$warning ? "#FFF7ED" : "#FAFAFA")};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  span:first-child {
    font-size: 13px;
    font-weight: 600;
    color: #181D27;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ProductLink = styled(Link)`
  min-width: 0;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-underline-offset: 3px;
  transition: color 0.12s, text-decoration-color 0.12s;

  &:hover,
  &:focus-visible {
    color: ${CARD_COLOR};
    text-decoration-color: ${CARD_COLOR};
  }
`;

const WarningBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: #C4320A;
  background: #FFF1E6;
  border-radius: 9999px;
  padding: 1px 6px;
  flex-shrink: 0;
`;

const MetricCellTrack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const MetricCellValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
`;

const MetricCellValue = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${(p) => (p.$dim ? "#C0C5D0" : "#101828")};
`;

const MetricCellBarTrack = styled.div`
  height: 4px;
  background: #F2F4F7;
  border-radius: 9999px;
  overflow: hidden;
  width: 100%;
`;

const MetricCellBarFill = styled.div`
  height: 100%;
  background: #7F56D9;
  border-radius: 9999px;
`;

const MetricCellLimit = styled.span`
  font-size: 9px;
  color: #C0C5D0;
`;

const MetricCell = ({ metric, showLimit = true }) => {
  if (!metric) {
    return (
      <MetricCellTrack>
        <MetricCellValue $dim>—</MetricCellValue>
      </MetricCellTrack>
    );
  }
  const isUnlimited = metric.limitText === "Unlimited";
  return (
    <MetricCellTrack>
      <MetricCellValueRow>
        <MetricCellValue>{metric.text}</MetricCellValue>
        {(showLimit || isUnlimited) && <MetricCellLimit>{metric.limitText}</MetricCellLimit>}
      </MetricCellValueRow>
      {showLimit && (
        <MetricCellBarTrack>
          <MetricCellBarFill style={{ width: `${metric.percent}%` }} />
        </MetricCellBarTrack>
      )}
    </MetricCellTrack>
  );
};

const TrendCell = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
`;

const EmptyTableNote = styled.div`
  padding: 24px 10px;
  text-align: center;
  font-size: 13px;
  color: #98A2B3;
`;

const Sidebar = styled.div`
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 1024px) {
    width: 100%;
  }
`;

const SidebarCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 1px 2px rgba(10, 13, 18, 0.04), 0 4px 12px rgba(10, 13, 18, 0.06);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SidebarTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SidebarTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #181D27;
`;

const TitleWithInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 0;
  color: #98A2B3;
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: #7F56D9;
  }
`;

const TooltipText = styled.span`
  display: block;
  max-width: 240px;
  line-height: 1.4;
`;

const TopProductsList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
`;

const LeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RankNumber = styled.span`
  width: 26px;
  font-size: 14px;
  font-weight: 700;
  color: #D4A8FA;
  flex-shrink: 0;
`;

const LeaderName = styled.span`
  flex: 1;
  font-size: 15px;
  font-weight: 600;
  color: #181D27;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LeaderMetricLabel = styled.span`
  font-size: 13px;
  font-weight: 400;
  color: #98A2B3;
`;

const LeaderValue = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #101828;
`;

const LeaderBarTrack = styled.div`
  height: 6px;
  background: #F2F4F7;
  border-radius: 9999px;
  overflow: hidden;
  margin-left: 82px;
  margin-top: 8px;
`;

const LeaderBarFill = styled.div`
  height: 100%;
  background: #7F56D9;
  border-radius: 9999px;
`;

const ViewBreakdownLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: auto;
  padding-top: 12px;
  border: none;
  border-top: 1px solid #F2F4F7;
  background: transparent;
  color: #7F56D9;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: #6941C6;
  }
`;

const AttentionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const AttentionTextCol = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const AttentionName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #181D27;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AttentionSub = styled.span`
  font-size: 12px;
  color: #98A2B3;
`;

const AttentionPercent = styled.span`
  font-size: 16px;
  font-weight: 800;
  color: #C4320A;
  flex-shrink: 0;
`;

const FeedRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 4px 0;
`;

const FeedDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 6px;
  flex-shrink: 0;
`;

const FeedText = styled.div`
  font-size: 13px;
  color: #344054;
`;

const FeedTime = styled.div`
  font-size: 11px;
  color: #98A2B3;
`;

const PinnedBadge = styled.span`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const NameIconBadge = styled.span`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: #F4EBFF;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 13px;
    height: 13px;
  }
`;
